import React, { createContext, useContext, useState } from 'react';
import CustomDialog from './CustomDialog';

const DialogContext = createContext();

export const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within DialogProvider');
  }
  return context;
};

export const DialogProvider = ({ children }) => {
  const [dialog, setDialog] = useState({
    show: false,
    type: 'confirm',
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    onConfirm: null,
    onCancel: null,
    icon: null
  });

  const showDialog = ({
    type = 'confirm',
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    icon = null
  }) => {
    return new Promise((resolve) => {
      setDialog({
        show: true,
        type,
        title,
        message,
        confirmText,
        cancelText,
        icon,
        onConfirm: () => {
          setDialog(prev => ({ ...prev, show: false }));
          resolve(true);
        },
        onCancel: () => {
          setDialog(prev => ({ ...prev, show: false }));
          resolve(false);
        }
      });
    });
  };

  // Convenience methods
  const confirm = ({ title = 'Are you sure?', message, confirmText = 'Confirm', cancelText = 'Cancel' }) => {
    return showDialog({ type: 'confirm', title, message, confirmText, cancelText });
  };

  const alert = ({ title, message, confirmText = 'OK' }) => {
    return showDialog({ type: 'alert', title, message, confirmText });
  };

  const confirmDelete = ({ title = 'Delete Item', message = 'Are you sure you want to delete this? This action cannot be undone.', confirmText = 'Delete', cancelText = 'Cancel' }) => {
    return showDialog({ type: 'delete', title, message, confirmText, cancelText });
  };

  const confirmLogout = ({ title = 'Logout', message = 'Are you sure you want to logout?', confirmText = 'Logout', cancelText = 'Cancel' }) => {
    return showDialog({ type: 'logout', title, message, confirmText, cancelText, icon: '👋' });
  };

  const success = ({ title = 'Success!', message, confirmText = 'OK' }) => {
    return showDialog({ type: 'success', title, message, confirmText });
  };

  const error = ({ title = 'Error', message, confirmText = 'OK' }) => {
    return showDialog({ type: 'error', title, message, confirmText });
  };

  const warning = ({ title = 'Warning', message, confirmText = 'OK', cancelText = 'Cancel' }) => {
    return showDialog({ type: 'warning', title, message, confirmText, cancelText });
  };

  return (
    <DialogContext.Provider value={{ 
      showDialog, 
      confirm, 
      alert, 
      confirmDelete, 
      confirmLogout,
      success,
      error,
      warning
    }}>
      {children}
      <CustomDialog {...dialog} />
    </DialogContext.Provider>
  );
};
